import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-hidden-fees')!;

export default function HiddenFees() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: The True Cost of Using Sugargoo
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          When shoppers first discover Sugargoo, they often focus on product prices and shipping quotes. But the <strong>total cost of using a shopping agent</strong> includes several layers of fees that are not always obvious at first glance. Understanding these hidden and semi-hidden costs upfront helps you budget accurately and avoid unpleasant surprises.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This guide exposes every fee you might encounter when using Sugargoo — from the clearly stated service charges to the easily overlooked costs like customs duties, payment processor fees, and currency conversion markups. More importantly, we share practical strategies to minimize or eliminate many of these charges.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          By the end, you will know exactly what to expect and how to keep your total order cost as low as possible.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          The Complete Fee Breakdown
        </h2>
        <div className="space-y-4">
          {[
            {
              title: 'Product Cost',
              desc: 'The base price of the item from the seller. This is the most visible cost and usually the smallest portion of your total expense for international orders.',
              hidden: false,
            },
            {
              title: 'Sugargoo Service Fee',
              desc: 'A percentage or fixed fee charged by Sugargoo for purchasing the item on your behalf. This fee covers the agent service, customer support, and platform maintenance. Typically ranges from 5-10% depending on the item category and order size.',
              hidden: false,
            },
            {
              title: 'Domestic Shipping (China)',
              desc: 'The cost of shipping the item from the seller to the Sugargoo warehouse. This is usually included in the product price or added as a small separate charge. Most domestic Chinese shipping is very affordable, often just a few yuan.',
              hidden: false,
            },
            {
              title: 'International Shipping',
              desc: 'The largest variable cost. Depends on weight, dimensions, destination, and shipping method. Can range from $15 for a small EUB package to $80+ for express delivery of multiple items.',
              hidden: false,
            },
            {
              title: 'QC Photo Fee',
              desc: 'Standard QC photos are free on Sugargoo. However, some agents charge for extra photos or detailed inspections. Always check what is included in your service package.',
              hidden: false,
            },
            {
              title: 'Warehouse Storage Fee',
              desc: 'Free for the standard storage period (usually 90-180 days). If you leave items beyond this window, daily storage fees apply. Avoid this by shipping within the free period.',
              hidden: true,
            },
            {
              title: 'Package Consolidation Fee',
              desc: 'Sugargoo generally does not charge for basic consolidation. Some value-added services like waterproof wrapping, corner protection, or vacuum sealing may have small fees.',
              hidden: true,
            },
            {
              title: 'Payment Processor Fees',
              desc: 'When topping up your Sugargoo balance, your payment method may charge fees. PayPal typically charges around 3-4%, credit cards vary, and Wise usually has the lowest rates.',
              hidden: true,
            },
            {
              title: 'Currency Conversion Markup',
              desc: 'If your payment is processed in a different currency than your bank account, you may pay a conversion fee or receive a less favorable exchange rate. This is charged by your bank or payment processor, not Sugargoo.',
              hidden: true,
            },
            {
              title: 'Customs Duties & Import Taxes',
              desc: 'Charged by your country customs authority, not Sugargoo. Rates vary dramatically by country and product type. USA has a high de minimis threshold; EU and UK apply VAT to most imports.',
              hidden: true,
            },
            {
              title: 'Carrier Handling Fees',
              desc: 'Some couriers charge brokerage or handling fees for processing customs on your behalf. DHL and FedEx are known for this in certain countries. Research your local carrier practices.',
              hidden: true,
            },
            {
              title: 'Insurance (Optional)',
              desc: 'Some shipping methods offer insurance for lost or damaged packages. This is usually optional but recommended for high-value orders. Costs around 1-3% of declared value.',
              hidden: true,
            },
          ].map((fee) => (
            <div key={fee.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-money-cny-circle-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">
                  {fee.title}
                  {fee.hidden && (
                    <span className="ml-2 px-2 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-medium rounded-full">
                      Often Overlooked
                    </span>
                  )}
                </h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{fee.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Country-Specific Cost Surprises
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The most common &quot;hidden fee&quot; complaints come from buyers who did not research their country import rules. Here is what to expect:
        </p>
        <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60 bg-background-100/50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Country</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Common Hidden Costs</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['USA', 'Generally duty-free under $800. No significant hidden fees for personal goods.'],
                ['UK', '20% VAT on most imports. Carrier handling fees (£8-12). No duty under £135 but VAT still applies.'],
                ['Canada', 'GST/HST (5-15%). Duties vary by product. Canada Post vs courier differences in fees.'],
                ['Australia', '10% GST on most imports. Low-value exemption removed in recent years.'],
                ['Germany / EU', 'VAT at local rate (19-27%). Customs duties by HS code. EORI for business shipments.'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-foreground-800">{row[0]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Always calculate the <strong>total landed cost</strong> — product + service fees + shipping + potential customs — before deciding whether an item is worth buying internationally.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          8 Strategies to Minimize Total Costs
        </h2>
        <div className="space-y-3">
          {[
            { title: 'Consolidate Everything', desc: 'Never ship single items internationally. Wait until you have 3+ items to spread shipping costs across multiple products.' },
            { title: 'Remove All Packaging', desc: 'Shoe boxes, tags, and excess materials add 20-40% to volumetric weight. Request removal for every fashion order.' },
            { title: 'Choose Economy Shipping', desc: 'SAL and EUB can cut shipping costs by 50-70% compared to DHL. The trade-off is time, not safety.' },
            { title: 'Use Wise for Payments', desc: 'Wise (TransferWise) consistently offers the lowest payment fees and best exchange rates for international transfers.' },
            { title: 'Plan Around Sales Events', desc: 'Chinese marketplace sales (11.11, 618 Festival) plus Sugargoo coupons can create double savings on the same order.' },
            { title: 'Stay Within Free Storage', desc: 'Ship before your free warehouse period expires. Storage fees are small but completely avoidable.' },
            { title: 'Research Customs Thresholds', desc: 'Know your country de minimis threshold. Ordering just under it can eliminate duties entirely.' },
            { title: 'Declare Value Accurately', desc: 'Under-declaring risks seizure. Over-declaring triggers unnecessary duties. Find the honest middle ground.' },
          ].map((tip, i) => (
            <div key={tip.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{tip.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Real-World Cost Example
        </h2>
        <div className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60">
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Example Order: 3 Hoodies + 2 T-Shirts</h4>
          <div className="space-y-2 text-sm">
            {[
              ['Product costs', '$45'],
              ['Sugargoo service fee (8%)', '$3.60'],
              ['Domestic shipping', '$2'],
              ['International shipping (EMS, 2.5kg)', '$38'],
              ['Payment fee (PayPal)', '$2.50'],
              ['Customs/VAT (UK example)', '$12'],
            ].map((row) => (
              <div key={row[0]} className="flex justify-between">
                <span className="text-foreground-600">{row[0]}</span>
                <span className="text-foreground-800 font-medium">{row[1]}</span>
              </div>
            ))}
            <div className="pt-2 border-t border-background-200/60 flex justify-between">
              <span className="font-semibold text-foreground-900">Total Landed Cost</span>
              <span className="font-semibold text-primary-600">~$103</span>
            </div>
          </div>
          <p className="text-xs text-foreground-500 mt-3">
            Compare this to buying similar quality items domestically — international shopping through an agent can still offer significant savings, but the total cost is higher than just the product price alone.
          </p>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Budget Smart, Shop Smart
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Sugargoo hidden fees</strong> are not actually hidden — they are simply costs that new shoppers do not anticipate because they are used to domestic e-commerce where shipping is often free and customs do not exist. The agent model has more moving parts, but each part is understandable once you know what to look for.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The key to cost-efficient international shopping is <strong>total cost awareness</strong>. Factor in every layer — product, service fees, shipping, payment fees, and potential customs — before placing your order. Use the strategies in this guide to minimize each cost category, and you will consistently achieve better value than unplanned, impulse ordering.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          With realistic budgeting and smart cost management, Sugargoo remains a powerful tool for accessing products and prices unavailable through traditional domestic shopping channels.
        </p>
      </section>
    </ArticleLayout>
  );
}